import { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

import API from 'utils/api';
import { PreviewCycleResponse } from 'utils/api/modules/schedules/schedules.types';

import { FrequencyType, formatDurationIso, generateRRule, wallClockDuration } from '../../utils/rruleUtils';

const DEBOUNCE_MS = 300;
const PREVIEW_COUNT = 3;

interface UsePreviewCyclesParams {
  frequency: FrequencyType;
  startDate: Date | null;
  dueDate: Date | null;
  zone?: string;
  dayZone?: string;
}

interface UsePreviewCyclesResult {
  cycles: PreviewCycleResponse[] | null;
  isLoading: boolean;
  error: string | null;
}

export function usePreviewCycles({
  frequency,
  startDate,
  dueDate,
  zone,
  dayZone,
}: UsePreviewCyclesParams): UsePreviewCyclesResult {
  const [cycles, setCycles] = useState<PreviewCycleResponse[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Race guard: only the latest request's response can update state.
  const requestIdRef = useRef(0);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (recurrence: string, duration: string, requestId: number) => {
        try {
          const { data } = await API.schedules.previewCycles({ recurrence, duration, count: PREVIEW_COUNT })();
          if (requestId === requestIdRef.current) {
            setCycles(data);
            setError(null);
            setIsLoading(false);
          }
        } catch {
          if (requestId === requestIdRef.current) {
            setError('Could not preview');
            setIsLoading(false);
          }
        }
      }, DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    const eligible =
      frequency !== 'one-off' && startDate !== null && dueDate !== null && dueDate.getTime() > startDate.getTime();

    if (!eligible || !startDate || !dueDate) {
      debouncedFetch.cancel();
      setCycles(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const rrule = generateRRule(frequency, startDate, zone, dayZone);
    const duration = zone ? wallClockDuration(startDate, dueDate, zone) : formatDurationIso(startDate, dueDate);

    requestIdRef.current += 1;
    setIsLoading(true);
    debouncedFetch(rrule, duration, requestIdRef.current);

    return () => {
      debouncedFetch.cancel();
    };
  }, [frequency, startDate?.getTime(), dueDate?.getTime(), zone, dayZone, debouncedFetch]);

  return { cycles, isLoading, error };
}
